import React, { useState, useEffect } from 'react';
import { Col, Row } from 'react-bootstrap';
import SearchableDropdown, { DropdownOption } from '../../../_common/SearchableDropdown/SearchableDropdown';
import {
    GET_ALL_ASSET_LOAD_TYPES,
    GET_ALL_LOCATIONS,
    GET_ALL_REGIONS,
    GET_ALL_TRAILERS,
    GET_ALL_TRAILER_TYPES,
    GET_DRIVERS_BY_REGION,
    GET_LOCATION_INFO,
} from '../../../../graphql/client/queries';
import { useQuery } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    GetAllAssetLoadTypes,
    GetAllLocations,
    GetAllRegions,
    GetAllTrailerTypes,
    GetAllTrailers,
    GetDriversByRegion,
    GetLocationInfo,
} from '../../../../types/schemaTypes';
import { blockInvalidChar } from '../../../_common/blockInvalidChar';
import { useAuthContext } from '../../../Auth/AuthProvider';
import { PermissionEnum } from '../../../../types/PermissionEnum';

interface AddLoadDriverDetailsProps {
    assetInfoExists: boolean;
    loadType: "Mob" | "Demob" | "New" | "Edit";
    assetLoadToSave: any | undefined;
    setAssetLoadToSave: React.Dispatch<React.SetStateAction<any>>;
    payCodeDropdownOptions: DropdownOption[];
    skipDropDowns: boolean;
    /** When Number of Loads > 1 on add (Mob/Demob/New), driver / truck / trailer must stay empty and disabled. */
    lockDriverIdentityFields?: boolean;
}

const AddLoadDriverDetails = ({
    assetInfoExists,
    loadType,
    assetLoadToSave,
    setAssetLoadToSave,
    payCodeDropdownOptions,
    skipDropDowns,
    lockDriverIdentityFields = false,
}: AddLoadDriverDetailsProps): React.ReactElement => {
    const isEditLoadEnabled = useAuthContext().userHasPermission(PermissionEnum.EditAssetLoadDetails);
    const isAssignLoadEnabled = useAuthContext().userHasPermission(PermissionEnum.AssignAssetLoads);
    const isAddEditAssetLoadNotesEnabled = useAuthContext().userHasPermission(PermissionEnum.AddEditAssetLoadNotes);
    const searchIcon = <FontAwesomeIcon icon={{ prefix: 'fas', iconName: 'search' }} />;

    const driverFieldsDisabled =
        !isAssignLoadEnabled || lockDriverIdentityFields;

    const latErrorMessage = 'The valid range is from -90 to 90.';
    const lngErrorMessage = 'The valid range is from -180 to 180.';
    const [isOriginLatError, setIsOriginLatError] = useState(false);
    const [isOriginLngError, setIsOriginLngError] = useState(false);
    const [isDestLatError, setIsDestLatError] = useState(false);
    const [isDestLngError, setIsDestLngError] = useState(false);

    const loadTypeDropdownOptions: DropdownOption[] = [];
    const { data: assetLoadTypes } = useQuery<GetAllAssetLoadTypes>(GET_ALL_ASSET_LOAD_TYPES);
    if (assetLoadTypes && assetLoadTypes.getAllAssetLoadTypes) {
        assetLoadTypes.getAllAssetLoadTypes.forEach(item => {
            if (!item) return;
            if (loadType === 'Mob' || loadType === 'Demob') {
                if (item.name === 'Mobilization Load' || item.name === 'Demobilization Load')
                    loadTypeDropdownOptions.push({ id: item.id, name: item.name });
            } else {
                loadTypeDropdownOptions.push({ id: item.id, name: item.name });
            }
        });
    }

    const originDestinationTypeDropdownOptions: DropdownOption[] = [{ id: 1, name: 'Job' }, { id: 2, name: 'Lofa' }, { id: 3, name: 'Yard' }, { id: 4, name: 'Other' }];

    const originDropdownOptions: DropdownOption[] = [];
    const destinationDropdownOptions: DropdownOption[] = [];

    // If Mob/ Demob from planning -- Send location ID to get Origin and Destination dropdown
    const { data: locationsData } = useQuery<GetAllLocations>(GET_ALL_LOCATIONS, {
        variables: {
            input: {
                locationType: "Job",
                locationId: loadType === "Mob" ? assetLoadToSave.destinationJobId : assetLoadToSave.originJobId,
            }
        },
        fetchPolicy: 'network-only',
        skip: loadType !== "Mob" && loadType !== "Demob",
    });

    // If Manual -- Send location ID as 0 when Origin is not "Other" to get the Origin dropdown
    const { data: originData } = useQuery<GetAllLocations>(GET_ALL_LOCATIONS, {
        variables: {
            input: {
                locationType: assetLoadToSave.originType,
                locationId: 0,
            }
        },
        fetchPolicy: 'network-only',
        skip: !assetLoadToSave.originType || assetLoadToSave.originType === "Other" || loadType === "Mob" || loadType === "Demob",
    });

    // If Manual -- Send Origin ID as Location ID. Send Location ID as 0 if Origin is "Other" or Load type is Transfer/ Move to get the Destination dropdown
    const { data: destinationData } = useQuery<GetAllLocations>(GET_ALL_LOCATIONS, {
        variables: {
            input: {
                locationType: assetLoadToSave.originType,
                locationId: ["Equipment Transfer", "Equipment Move"].includes(assetLoadToSave.assetLoadType) ? 0 : (
                    assetLoadToSave.originType === "Job" ? assetLoadToSave.originJobId : (
                        assetLoadToSave.originType === "Lofa" ? assetLoadToSave.originLofaId : (
                            assetLoadToSave.originType === "Yard" ? assetLoadToSave.originYardId : (
                                0
                            )
                        )
                    )
                ),
            }
        },
        fetchPolicy: 'network-only',
        skip: !assetLoadToSave.destinationType || !assetLoadToSave.originType ||
            loadType === "Mob" || loadType === "Demob" ||
            (assetLoadToSave.originType === "Job" && !assetLoadToSave.originJobId) ||
            (assetLoadToSave.originType === "Lofa" && !assetLoadToSave.originLofaId) ||
            (assetLoadToSave.originType === "Yard" && !assetLoadToSave.originYardId)
    });

    if (locationsData && locationsData.getAllLocations) {
        locationsData.getAllLocations.map(item => {
            if (item && item.locationId && item.descrip) {
                if (loadType === "Mob" && item.locationType === assetLoadToSave.originType)
                    originDropdownOptions.push({
                        id: item.locationId,
                        name: item.descrip,
                    })
                if (loadType === "Demob" && item.locationType === assetLoadToSave.destinationType)
                    destinationDropdownOptions.push({
                        id: item.locationId,
                        name: item.descrip,
                    })
            }
        })
    }

    if (originData && originData.getAllLocations) {
        originData.getAllLocations.map(item => {
            if (item && item.locationType === assetLoadToSave.originType && item.locationId && item.descrip && loadType !== "Mob" && loadType !== "Demob") {
                originDropdownOptions.push({
                    id: item.locationId,
                    name: item.descrip,
                })
            }
        })
    }

    if (destinationData && destinationData.getAllLocations) {
        destinationData.getAllLocations.map(item => {
            if (item && item.locationType === assetLoadToSave.destinationType && item.locationId && item.descrip && loadType !== "Mob" && loadType !== "Demob") {
                destinationDropdownOptions.push({
                    id: item.locationId,
                    name: item.descrip,
                })
            }
        })
    }

    const { data: locationInfoData } = useQuery<GetLocationInfo>(GET_LOCATION_INFO, {
        variables: {
            input: {
                originId: assetLoadToSave.originType === "Job" ?
                    (
                        assetLoadToSave.originJobId ?? -1
                    ) : (
                        assetLoadToSave.originType === "Lofa" ?
                            (
                                assetLoadToSave.originLofaId ?? -1
                            ) : (
                                assetLoadToSave.originType === "Yard" ?
                                    (
                                        assetLoadToSave.originYardId ?? -1
                                    ) : (
                                        -1
                                    )
                            )
                    ),
                originType: assetLoadToSave?.originType?.length === 0 ? "-1" : assetLoadToSave.originType,
                destinationId: assetLoadToSave.destinationType === "Job" ?
                    (
                        assetLoadToSave.destinationJobId ?? -1
                    ) : (
                        assetLoadToSave.destinationType === "Lofa" ?
                            (
                                assetLoadToSave.destinationLofaId ?? -1
                            ) : (
                                assetLoadToSave.destinationType === "Yard" ?
                                    (
                                        assetLoadToSave.destinationYardId ?? -1
                                    ) : (
                                        -1
                                    )
                            )
                    ),
                destinationType: assetLoadToSave?.destinationType?.length === 0 ? "-1" : assetLoadToSave.destinationType,
            }
        },
        fetchPolicy: "network-only",
        skip: (assetLoadToSave.originType && assetLoadToSave?.originType?.length !== 0 && assetLoadToSave?.originType !== "Other" && !assetLoadToSave.originJobId && !assetLoadToSave.originLofaId && !assetLoadToSave.originYardId) ||
            (assetLoadToSave.destinationType && assetLoadToSave.destinationType.length !== 0 && assetLoadToSave?.destinationType !== "Other" && assetLoadToSave.destinationType !== "-1" && !assetLoadToSave.destinationJobId && !assetLoadToSave.destinationLofaId && !assetLoadToSave.destinationYardId)
    });

    useEffect(() => {
        if (locationInfoData?.getLocationInfo) {
            const values = locationInfoData.getLocationInfo;
            const tempAssetLoadToSave = { ...assetLoadToSave };
            if (values.originRegionId) {
                tempAssetLoadToSave.originRegionId = values.originRegionId;
                tempAssetLoadToSave.originRegionDescrip = values.originRegionDescrip;
            }
            if (values.originDistrictId) {
                tempAssetLoadToSave.originDistrictId = values.originDistrictId;
                tempAssetLoadToSave.originDistrictDescrip = values.originDistrictDescrip;
            }
            if (values.destinationRegionId) {
                tempAssetLoadToSave.destinationRegionId = values.destinationRegionId;
                tempAssetLoadToSave.destinationRegionDescrip = values.destinationRegionDescrip;
            }
            if (values.destinationDistrictId) {
                tempAssetLoadToSave.destinationDistrictId = values.destinationDistrictId;
                tempAssetLoadToSave.destinationDistrictDescrip = values.destinationDistrictDescrip;
            }
            setAssetLoadToSave(tempAssetLoadToSave);
        }
    }, [locationInfoData]);

    const regionDropdownOptions: DropdownOption[] = [];
    const { data: regions } = useQuery<GetAllRegions>(GET_ALL_REGIONS, { skip: skipDropDowns });
    if (regions && regions.getAllRegions) {
        regions.getAllRegions.map(item => {
            if (item?.id && item?.regionName) {
                // If Equipment transfer then remove origin region from the list
                if (assetLoadToSave.assetLoadType !== "Equipment Transfer" || item.id !== assetLoadToSave.originRegionId)
                    regionDropdownOptions.push({
                        id: item.id,
                        name: item.regionName,
                    });
            }
        });
    }

    const originDistrictDropdownOptions: DropdownOption[] = [];
    if (assetLoadToSave?.originRegionId && regions?.getAllRegions) {
        const selectedRegion = regions.getAllRegions.find(item => item != null && item.id === assetLoadToSave?.originRegionId);
        (selectedRegion?.districts ?? []).forEach(item => {
            if (item?.id != null && item?.districtName) {
                originDistrictDropdownOptions.push({
                    id: item.id,
                    name: item.districtName,
                });
            }
        });
    }

    const destinationDistrictDropdownOptions: DropdownOption[] = [];
    if (assetLoadToSave?.destinationRegionId && regions?.getAllRegions) {
        const selectedRegion = regions.getAllRegions.find(item => item != null && item.id === assetLoadToSave?.destinationRegionId);
        (selectedRegion?.districts ?? []).forEach(item => {
            if (item?.id != null && item?.districtName) {
                destinationDistrictDropdownOptions.push({
                    id: item.id,
                    name: item.districtName,
                });
            }
        });
    }

    const trailerTypeDropdownOptions: DropdownOption[] = [];
    const { data: trailerTypes } = useQuery<GetAllTrailerTypes>(GET_ALL_TRAILER_TYPES);
    if (trailerTypes && trailerTypes.getAllTrailerTypes) {
        trailerTypes.getAllTrailerTypes.forEach(item => {
            if (item) trailerTypeDropdownOptions.push({ id: item.id, name: item.name });
        });
    }

    const driverDropdownOptions: DropdownOption[] = [];
    const { data: drivers } = useQuery<GetDriversByRegion>(GET_DRIVERS_BY_REGION, {
        variables: {
            input: {
                regionId: assetLoadToSave.originRegionId ?? 0,
                districtId: assetLoadToSave.originDistrictId ?? 0
            }
        },
        fetchPolicy: "network-only",
        skip: !isAssignLoadEnabled,
    });
    if (drivers && drivers.getDriversByRegion) {
        drivers.getDriversByRegion.map(item => {
            if (item && item.driverName && item.id)
                driverDropdownOptions.push({
                    id: item.id,
                    name: item.driverName,
                })
        })
    }

    const trailerDropdownOptions: DropdownOption[] = [];
    const { data: trailerData, loading: trailerDataLoading } = useQuery<GetAllTrailers>(GET_ALL_TRAILERS, {
        variables: {
            input: {
                trailerTypeId: assetLoadToSave?.trailerTypeId,
                assetLoadId: assetLoadToSave?.id ?? 0,
            }
        },
        fetchPolicy: "network-only",
        skip: !assetLoadToSave?.trailerTypeId,
    });
    if (trailerData && trailerData.getAllTrailers) {
        trailerData.getAllTrailers.forEach(item => {
            if (item) trailerDropdownOptions.push({ id: item.id, name: item.name });
        });
    }

    const updateAssetLoad = (inputTypes: string[], values: any[]) => {
        const tempAssetLoadToSave = { ...assetLoadToSave };
        inputTypes.map((inputType, index) => {
            tempAssetLoadToSave[inputType] = values[index];
        })
        setAssetLoadToSave(tempAssetLoadToSave);
    }

    const handleOnChangeLoadType = (assetLoadTypeId: number) => {
        let assetLoadType = '';
        loadTypeDropdownOptions.map(item => {
            if (item.id === assetLoadTypeId)
                assetLoadType = item.name.toString()
        })

        if (loadType === "Mob" || loadType === "Demob") {
            updateAssetLoad(['assetLoadTypeId', 'assetLoadType'], [assetLoadTypeId, assetLoadType,]);
        }
        else {
            // If 'Equipment Transfer' then reset Destination
            if (assetLoadType === "Equipment Transfer")
                updateAssetLoad(
                    ['assetLoadTypeId', 'assetLoadType', 'destinationType', 'destinationJobId', 'destinationJob', 'destinationLofaId', 'destinationLofa', 'destinationYardId', 'destinationYard', 'destinationLongitude', 'destinationLatitude', 'destinationRegionId', 'destinationRegionDescrip', 'destinationDistrictId', 'destinationDistrictDescrip'],
                    [assetLoadTypeId, assetLoadType, null, null, null, null, null, null, null, null, null, null, null, null, null]
                )
            // If 'Equipment Move' then reset Destination with Same region as Origin
            else if (assetLoadType === "Equipment Move")
                updateAssetLoad(
                    ['assetLoadTypeId', 'assetLoadType', 'destinationType', 'destinationJobId', 'destinationJob', 'destinationLofaId', 'destinationLofa', 'destinationYardId', 'destinationYard', 'destinationLongitude', 'destinationLatitude', 'destinationRegionId', 'destinationRegionDescrip', 'destinationDistrictId', 'destinationDistrictDescrip'],
                    [assetLoadTypeId, assetLoadType, null, null, null, null, null, null, null, null, null, assetLoadToSave.originRegionId, assetLoadToSave.originRegionDescrip, null, null]
                )
            else
                updateAssetLoad(
                    ['assetLoadTypeId', 'assetLoadType'],
                    [assetLoadTypeId, assetLoadType]
                )
        }
    }

    const handleOnChangeTerminalType = (value: number, isOrigin: boolean) => {
        let terminalType = '';
        if (isOrigin) {
            originDestinationTypeDropdownOptions.map(item => {
                if (item.id === value)
                    terminalType = item.name.toString();
            })
            updateAssetLoad(
                ['originType', 'originJobId', 'originJob', 'originLofaId', 'originLofa', 'originYardId', 'originYard', 'originLongitude', 'originLatitude', 'originRegionId', 'originRegionDescrip', 'originDistrictId', 'originDistrictDescrip'],
                [terminalType, null, null, null, null, null, null, null, null, null, null, null, null]
            );
        }
        else {
            originDestinationTypeDropdownOptions.map(item => {
                if (item.id === value)
                    terminalType = item.name.toString();
            })
            updateAssetLoad(
                ['destinationType', 'destinationJobId', 'destinationJob', 'destinationLofaId', 'destinationLofa', 'destinationYardId', 'destinationYard', 'destinationLongitude', 'destinationLatitude', 'destinationRegionId', 'destinationRegionDescrip', 'destinationDistrictId', 'destinationDistrictDescrip'],
                [terminalType, null, null, null, null, null, null, null, null, null, null, null, null]
            );
        }
    }

    const handleOnChangeTerminal = (terminalId: number | null, isOrigin: boolean) => {
        let terminal = '';
        if (terminalId === 0) terminalId = null;
        if (isOrigin) {
            originDropdownOptions.map(item => {
                if (item.id === terminalId)
                    terminal = item.name.toString()
            })

            if (assetLoadToSave?.originType === 'Job')
                updateAssetLoad(
                    ['originJobId', 'originJob', 'originLofaId', 'originLofa', 'originYardId', 'originYard', 'originLongitude', 'originLatitude'],
                    [terminalId, terminal, null, null, null, null, null, null, null,]
                );
            if (assetLoadToSave?.originType === 'Lofa')
                updateAssetLoad(
                    ['originJobId', 'originJob', 'originLofaId', 'originLofa', 'originYardId', 'originYard', 'originLongitude', 'originLatitude'],
                    [null, null, terminalId, terminal, null, null, null, null, null,]
                );
            if (assetLoadToSave?.originType === 'Yard')
                updateAssetLoad(
                    ['originJobId', 'originJob', 'originLofaId', 'originLofa', 'originYardId', 'originYard', 'originLongitude', 'originLatitude'],
                    [null, null, null, null, terminalId, terminal, null, null, null,]
                );
        }
        else {
            destinationDropdownOptions.map(item => {
                if (item.id === terminalId)
                    terminal = item.name.toString();
            })

            if (assetLoadToSave?.destinationType === 'Job')
                updateAssetLoad(
                    ['destinationJobId', 'destinationJob', 'destinationLofaId', 'destinationLofa', 'destinationYardId', 'destinationYard', 'destinationLongitude', 'destinationLatitude'],
                    [terminalId, terminal, null, null, null, null, null, null, null,]
                );
            if (assetLoadToSave?.destinationType === 'Lofa')
                updateAssetLoad(
                    ['destinationJobId', 'destinationJob', 'destinationLofaId', 'destinationLofa', 'destinationYardId', 'destinationYard', 'destinationLongitude', 'destinationLatitude'],
                    [null, null, terminalId, terminal, null, null, null, null, null,]
                );
            if (assetLoadToSave?.destinationType === 'Yard')
                updateAssetLoad(
                    ['destinationJobId', 'destinationJob', 'destinationLofaId', 'destinationLofa', 'destinationYardId', 'destinationYard', 'destinationLongitude', 'destinationLatitude'],
                    [null, null, null, null, terminalId, terminal, null, null, null,]
                );
        }
    }

    const handleOnChangeOriginLat = (value: string) => {
        const lat = parseFloat(value);
        if (lat < -90 || lat > 90) setIsOriginLatError(true);
        else setIsOriginLatError(false);
        if (Number.isNaN(lat))
            updateAssetLoad(['originLatitude'], [null]);
        else
            updateAssetLoad(['originLatitude'], [lat]);
    }

    const handleOnChangeOriginLong = (value: string) => {
        const long = parseFloat(value);
        if (long < -180 || long > 180) setIsOriginLngError(true);
        else setIsOriginLngError(false);
        if (Number.isNaN(long))
            updateAssetLoad(['originLongitude'], [null]);
        else
            updateAssetLoad(['originLongitude'], [long]);
    }

    const handleOnChangeDestinationLat = (value: string) => {
        const lat = parseFloat(value);
        if (lat < -90 || lat > 90) setIsDestLatError(true);
        else setIsDestLatError(false);
        if (Number.isNaN(lat))
            updateAssetLoad(['destinationLatitude'], [null]);
        else
            updateAssetLoad(['destinationLatitude'], [lat]);
    }

    const handleOnChangeDestinationLong = (value: string) => {
        const long = parseFloat(value);
        if (long < -180 || long > 180) setIsDestLngError(true);
        else setIsDestLngError(false);
        if (Number.isNaN(long))
            updateAssetLoad(['destinationLongitude'], [null]);
        else
            updateAssetLoad(['destinationLongitude'], [long]);
    }

    const handleOnChangeRegion = (regionId: number, isOrigin: boolean) => {
        let region = '';
        if (isOrigin) {
            regionDropdownOptions.map(item => {
                if (item.id === regionId)
                    region = item.name.toString();
            })
            updateAssetLoad(
                ['originRegionId', 'originRegionDescrip', 'originDistrictId', 'originDistrictDescrip'],
                [regionId, region, null, null]
            );
        }
        else {
            regionDropdownOptions.map(item => {
                if (item.id === regionId)
                    region = item.name.toString();
            })
            updateAssetLoad(
                ['destinationRegionId', 'destinationRegionDescrip', 'destinationDistrictId', 'destinationDistrictDescrip'],
                [regionId, region, null, null]
            );
        }
    }

    const handleOnChangeDistrict = (districtId: number, isOrigin: boolean) => {
        let district = '';
        if (isOrigin) {
            originDistrictDropdownOptions.map(item => {
                if (item.id === districtId)
                    district = item.name.toString();
            })
            updateAssetLoad(
                ['originDistrictId', 'originDistrictDescrip'],
                [districtId, district]
            );
        }
        else {
            destinationDistrictDropdownOptions.map(item => {
                if (item.id === districtId)
                    district = item.name.toString();
            })
            updateAssetLoad(
                ['destinationDistrictId', 'destinationDistrictDescrip'],
                [districtId, district]
            );
        }
    }

    const handleOnChangeTrailerType = (trailerTypeId: number | null) => {
        if (trailerTypeId === 0) trailerTypeId = null;
        let trailerType = '';
        trailerTypeDropdownOptions.map(item => {
            if (item.id === trailerTypeId)
                trailerType = item.name.toString();
        })

        // If Trailer type is 'Stepdeck' then Paycode must be 'Stepdeck'
        if (trailerType === 'Stepdeck') {
            updateAssetLoad(
                ['trailerTypeId', 'trailerType', 'trailerId', 'trailer', 'payCode'],
                [trailerTypeId, trailerType, null, null, 'Stepdeck']
            );
        }
        // If Trailer type is 'Chassis' then Paycode must be 'Single Empty Box'
        else if (trailerType === 'Sandbox Chassis') {
            updateAssetLoad(
                ['trailerTypeId', 'trailerType', 'trailerId', 'trailer', 'payCode'],
                [trailerTypeId, trailerType, null, null, 'Single Empty Box']
            );
        }
        // If Trailer type is 'Cradle' then Paycode must be 'Cradle'
        else if (trailerType === 'Cradle') {
            updateAssetLoad(
                ['trailerTypeId', 'trailerType', 'trailerId', 'trailer', 'payCode'],
                [trailerTypeId, trailerType, null, null, 'Cradle']
            );
        }
        else {
            updateAssetLoad(
                ['trailerTypeId', 'trailerType', 'trailerId', 'trailer'],
                [trailerTypeId, trailerType, null, null]
            );
        }
    }

    const handleOnChangePayCode = (payCodeId: number) => {
        let payCode = '';
        payCodeDropdownOptions.map(item => {
            if (item.id === payCodeId)
                payCode = item.name.toString();
        })
        updateAssetLoad(
            ['payCode'],
            [payCode]
        );
    }

    const handleOnChangeMiles = (value: string) => {
        const miles = parseInt(value);
        if (miles < 0) return;
        if (Number.isNaN(miles) || !Number.isInteger(miles))
            updateAssetLoad(['miles'], [null]);
        else
            updateAssetLoad(['miles'], [miles]);
    }

    const handleOnChangeAccessorialCost = (value: string) => {
        const cost = parseInt(value);
        if (cost < 0) return;
        if (Number.isNaN(cost) || !Number.isInteger(cost))
            updateAssetLoad(['accessorialCost'], [null]);
        else
            updateAssetLoad(['accessorialCost'], [cost]);
    }

    const handleOnChangeDriver = (driverId: number | null) => {
        if (!isAssignLoadEnabled || lockDriverIdentityFields) return;
        let driverName = '';
        if (driverId === 0) driverId = null;
        driverDropdownOptions.map(item => {
            if (item.id === driverId)
                driverName = item.name.toString();
        })
        let trailerId: number | null = null;
        let trailer: string = '';
        drivers?.getDriversByRegion?.map(item => {
            if (item?.id === driverId) {
                trailerId = item.trailerId ?? null;
                trailer = item.trailerDescrip ?? '';
            }
        })
        updateAssetLoad(['driverId', 'driverName', 'trailerId', 'trailer'], [driverId, driverName, trailerId, trailer]);
    }

    const handleOnChangeTruckNumber = (truckNumber) => {
        if (!isAssignLoadEnabled || lockDriverIdentityFields) return;
        if (truckNumber)
            updateAssetLoad(['truckNumber'], [truckNumber]);
        else
            updateAssetLoad(['truckNumber'], [null]);
    }

    const handleOnChangeTrailer = (trailerId: number | null) => {
        if (!isAssignLoadEnabled || lockDriverIdentityFields) return;
        let trailer = '';
        if (trailerId === 0) trailerId = null;
        trailerDropdownOptions.map(item => {
            if (item.id === trailerId)
                trailer = item.name.toString();
        })
        updateAssetLoad(['trailerId', 'trailer'], [trailerId, trailer]);
    }

    const isTrailerInvalid = () => {
        let isTrailerInvalid = true;
        if (!assetLoadToSave?.driverName)
            isTrailerInvalid = false;
        else
            trailerDropdownOptions.map(item => {
                if (assetLoadToSave.trailer === item.name) isTrailerInvalid = false;
            })
        return isTrailerInvalid;
    }

    const handleOnChangeNotes = (notes: string | null) => {
        if (notes?.length === 0)
            updateAssetLoad(['notes'], [null]);
        else
            updateAssetLoad(['notes'], [notes]);
    }

    const LOAD_COUNT_DROPDOWN_MAX = 20;
    const loadCountDropdownOptions: DropdownOption[] = Array.from({ length: LOAD_COUNT_DROPDOWN_MAX }, (_, i) => ({
        id: i + 1,
        name: String(i + 1),
    }));

    return (
        <>
            <div className='AddAssetDetails-Container'>
                <div className='AddAssetDetails-Header'>
                    Load Details
                </div>

                <div className='AddAssetDetails-Body'>
                    <Row>
                        <Col md={3} className='AddAssetDetails-Field'>
                            <SearchableDropdown
                                dropdownOptions={loadTypeDropdownOptions}
                                id="loadType"
                                label="LOAD TYPE"
                                onSelect={value => { value && handleOnChangeLoadType(parseInt(value)) }}
                                value={assetLoadToSave?.assetLoadType}
                                required
                                disabled={loadType === 'Edit' && !isEditLoadEnabled}
                            />
                        </Col>
                        {loadType !== 'Edit' && (
                            <Col md={3} className='AddAssetDetails-Field'>
                                <SearchableDropdown
                                    dropdownOptions={loadCountDropdownOptions}
                                    id="loadCount"
                                    label="NUMBER OF LOADS"
                                    onSelect={value => {
                                        if (value != null)
                                            updateAssetLoad(['loadCount'], [parseInt(String(value), 10)]);
                                    }}
                                    value={assetLoadToSave?.loadCount != null ? String(assetLoadToSave.loadCount) : '1'}
                                    required
                                />
                            </Col>
                        )}
                        <Col md={3} className='AddAssetDetails-Field'>
                            <div className="Textfield-InputTitle input-group-text AddAssetDetails-InputNumber-Container">
                                *LOAD STATUS
                            </div>
                            <input
                                type="text"
                                title="loadStatus"
                                className="AddAssetDetails-Number AddAssetDetails-InputNumber Textfield-Disabled"
                                value={assetLoadToSave?.status ?? ''}
                                disabled
                                onChange={element => { }}
                            />
                        </Col>
                    </Row>

                    <Row>
                        <Col md={3} className='AddAssetDetails-Field'>
                            <SearchableDropdown
                                dropdownOptions={originDestinationTypeDropdownOptions}
                                id="originType"
                                label="ORIGIN TYPE"
                                onSelect={value => { value && handleOnChangeTerminalType(parseInt(value), true) }}
                                value={assetLoadToSave?.originType}
                                required
                                disabled={loadType === "Demob" || skipDropDowns || assetInfoExists || (loadType === 'Edit' && !isEditLoadEnabled)}
                            />
                        </Col>
                        {assetLoadToSave?.originType === 'Other' ? (
                            <>
                                <Col md={2} className='AddAssetDetails-Field'>
                                    <div className="Textfield-InputTitle input-group-text AddAssetDetails-InputNumber-Container">
                                        {assetLoadToSave.originLatitude || assetLoadToSave.originLongitude ? '*LATITUDE' : 'LATITUDE'}
                                    </div>
                                    <input
                                        type="number"
                                        title="latitude"
                                        className="AddAssetDetails-Number AddAssetDetails-InputNumber"
                                        value={assetLoadToSave?.originLatitude ?? ''}
                                        onChange={element => { handleOnChangeOriginLat(element.target.value) }}
                                        onKeyDown={e => ['e', 'E'].includes(e.key) && e.preventDefault()}
                                        disabled={loadType === "Demob" || skipDropDowns || assetInfoExists || (loadType === 'Edit' && !isEditLoadEnabled)}
                                    />
                                    {isOriginLatError && <span className="AddAssetDetails-CoordsError">{latErrorMessage}</span>}
                                </Col>
                                <Col md={2} className='AddAssetDetails-Field'>
                                    <div className="Textfield-InputTitle input-group-text AddAssetDetails-InputNumber-Container">
                                        {assetLoadToSave.originLatitude || assetLoadToSave.originLongitude ? '*LONGITUDE' : 'LONGITUDE'}
                                    </div>
                                    <input
                                        type="number"
                                        title="longitude"
                                        className="AddAssetDetails-Number AddAssetDetails-InputNumber"
                                        value={assetLoadToSave?.originLongitude ?? ''}
                                        onChange={element => { handleOnChangeOriginLong(element.target.value) }}
                                        onKeyDown={e => ['e', 'E'].includes(e.key) && e.preventDefault()}
                                        disabled={loadType === "Demob" || skipDropDowns || assetInfoExists || (loadType === 'Edit' && !isEditLoadEnabled)}
                                    />
                                    {isOriginLngError && <span className="AddAssetDetails-CoordsError">{lngErrorMessage}</span>}
                                </Col>
                            </>
                        ) : (
                            <Col md={3} className='AddAssetDetails-Field'>
                                <SearchableDropdown
                                    dropdownOptions={originDropdownOptions}
                                    id="origin"
                                    label="ORIGIN"
                                    onSelect={value => { value && handleOnChangeTerminal(parseInt(value), true) }}
                                    value={assetLoadToSave?.originJob ?? assetLoadToSave?.originLofa ?? assetLoadToSave?.originYard ?? ' '}
                                    required
                                    autoCompleteEnabled
                                    endIcon={searchIcon}
                                    disabled={!assetLoadToSave?.originType || loadType === "Demob" || skipDropDowns || assetInfoExists || (loadType === 'Edit' && !isEditLoadEnabled)}
                                />
                            </Col>
                        )}
                        <Col md={assetLoadToSave?.originType === 'Other' ? 2 : 3} className='AddAssetDetails-Field'>
                            <SearchableDropdown
                                dropdownOptions={regionDropdownOptions}
                                id="originRegion"
                                label="ORIGIN REGION"
                                onSelect={value => { value && handleOnChangeRegion(parseInt(value), true) }}
                                value={assetLoadToSave?.originRegionDescrip}
                                required
                                disabled={loadType === "Demob" || skipDropDowns || assetInfoExists || (loadType === 'Edit' && !isEditLoadEnabled)}
                            />
                        </Col>
                        <Col md={3} className='AddAssetDetails-Field'>
                            <SearchableDropdown
                                dropdownOptions={originDistrictDropdownOptions}
                                id="originDistrict"
                                label="ORIGIN DISTRICT"
                                onSelect={value => { value && handleOnChangeDistrict(parseInt(value), true) }}
                                value={assetLoadToSave?.originDistrictDescrip}
                                disabled={!assetLoadToSave?.originRegionId || loadType === "Demob" || skipDropDowns || assetInfoExists || (loadType === 'Edit' && !isEditLoadEnabled)}
                                required
                            />
                        </Col>
                    </Row>

                    <Row>
                        <Col md={3} className='AddAssetDetails-Field'>
                            <SearchableDropdown
                                dropdownOptions={originDestinationTypeDropdownOptions}
                                id="destinationType"
                                label="DESTINATION TYPE"
                                onSelect={value => { value && handleOnChangeTerminalType(parseInt(value), false) }}
                                value={assetLoadToSave?.destinationType}
                                required
                                disabled={loadType === "Mob" || skipDropDowns || (loadType === 'Edit' && !isEditLoadEnabled)}
                            />
                        </Col>
                        {assetLoadToSave?.destinationType === 'Other' ? (
                            <>
                                <Col md={2} className='AddAssetDetails-Field'>
                                    <div className="Textfield-InputTitle input-group-text AddAssetDetails-InputNumber-Container">
                                        *LATITUDE
                                    </div>
                                    <input
                                        type="number"
                                        title="latitude"
                                        className="AddAssetDetails-Number AddAssetDetails-InputNumber"
                                        value={assetLoadToSave?.destinationLatitude ?? ''}
                                        onChange={element => { handleOnChangeDestinationLat(element.target.value) }}
                                        onKeyDown={e => ['e', 'E'].includes(e.key) && e.preventDefault()}
                                        disabled={loadType === "Mob" || skipDropDowns || (loadType === 'Edit' && !isEditLoadEnabled)}
                                    />
                                    {isDestLatError && <span className="AddAssetDetails-CoordsError">{latErrorMessage}</span>}
                                </Col>
                                <Col md={2} className='AddAssetDetails-Field'>
                                    <div className="Textfield-InputTitle input-group-text AddAssetDetails-InputNumber-Container">
                                        *LONGITUDE
                                    </div>
                                    <input
                                        type="number"
                                        title="longitude"
                                        className="AddAssetDetails-Number AddAssetDetails-InputNumber"
                                        value={assetLoadToSave?.destinationLongitude ?? ''}
                                        onChange={element => { handleOnChangeDestinationLong(element.target.value) }}
                                        onKeyDown={e => ['e', 'E'].includes(e.key) && e.preventDefault()}
                                        disabled={loadType === "Mob" || skipDropDowns || (loadType === 'Edit' && !isEditLoadEnabled)}
                                    />
                                    {isDestLngError && <span className="AddAssetDetails-CoordsError">{lngErrorMessage}</span>}
                                </Col>
                            </>
                        ) : (
                            <Col md={3} className='AddAssetDetails-Field'>
                                <SearchableDropdown
                                    dropdownOptions={destinationDropdownOptions}
                                    id="destination"
                                    label="DESTINATION"
                                    onSelect={value => { value && handleOnChangeTerminal(parseInt(value), false) }}
                                    value={assetLoadToSave?.destinationJob ?? assetLoadToSave?.destinationLofa ?? assetLoadToSave?.destinationYard ?? ' '}
                                    required
                                    autoCompleteEnabled
                                    endIcon={searchIcon}
                                    disabled={!assetLoadToSave?.destinationType || loadType === "Mob" || skipDropDowns || (loadType === 'Edit' && !isEditLoadEnabled)}
                                />
                            </Col>
                        )}
                        <Col md={assetLoadToSave?.destinationType === "Other" ? 2 : 3} className='AddAssetDetails-Field'>
                            <SearchableDropdown
                                dropdownOptions={regionDropdownOptions}
                                id="destinationRegion"
                                label="DESTINATION REGION"
                                onSelect={value => { value && handleOnChangeRegion(parseInt(value), false); }}
                                value={assetLoadToSave?.destinationRegionDescrip}
                                required
                                disabled={loadType === "Mob" || skipDropDowns || (loadType === 'Edit' && !isEditLoadEnabled)}
                            />
                        </Col>
                        <Col md={3} className='AddAssetDetails-Field'>
                            <SearchableDropdown
                                dropdownOptions={destinationDistrictDropdownOptions}
                                id="destinationDistrict"
                                label="DESTINATION DISTRICT"
                                onSelect={value => { value && handleOnChangeDistrict(parseInt(value), false) }}
                                value={assetLoadToSave?.destinationDistrictDescrip}
                                required
                                disabled={!assetLoadToSave?.destinationRegionId || loadType === "Mob" || skipDropDowns || (loadType === 'Edit' && !isEditLoadEnabled)}
                            />
                        </Col>
                    </Row>

                    <Row>
                        <Col md={3} className='AddAssetDetails-Field'>
                            <SearchableDropdown
                                dropdownOptions={trailerTypeDropdownOptions}
                                id="trailerType"
                                label="TRAILER TYPE"
                                onSelect={value => { value && handleOnChangeTrailerType(parseInt(value)) }}
                                value={assetLoadToSave?.trailerType}
                                autoCompleteEnabled
                                endIcon={searchIcon}
                                required
                                disabled={loadType === 'Edit' && !isEditLoadEnabled}
                            />
                        </Col>
                        <Col md={3} className='AddAssetDetails-Field'>
                            <SearchableDropdown
                                dropdownOptions={payCodeDropdownOptions}
                                id="payCode"
                                label="PAY CODE"
                                onSelect={value => { value && handleOnChangePayCode(parseInt(value)) }}
                                value={assetLoadToSave?.payCode}
                                disabled={loadType === 'Edit' && !isEditLoadEnabled}
                            />
                        </Col>
                        <Col md={3} className='AddAssetDetails-Field'>
                            <div className="Textfield-InputTitle input-group-text AddAssetDetails-InputNumber-Container">
                                {assetLoadToSave?.trailerTypeId === 6 ? "*MILES" : "MILES"}
                            </div>
                            <input
                                type="number"
                                title="miles"
                                className="AddAssetDetails-Number AddAssetDetails-InputNumber"
                                value={assetLoadToSave?.miles ?? ''}
                                onChange={element => { handleOnChangeMiles(element.target.value) }}
                                onKeyDown={blockInvalidChar}
                                onWheel={event => event.currentTarget.blur()}
                                disabled={loadType === 'Edit' && !isEditLoadEnabled}
                            />
                        </Col>
                        <Col md={3} className='AddAssetDetails-Field'>
                            <div className="Textfield-InputTitle input-group-text AddAssetDetails-InputNumber-Container">
                                ACCESSORIAL COST
                            </div>
                            <input
                                type="number"
                                title="accessorialCost"
                                className="AddAssetDetails-Number AddAssetDetails-InputNumber"
                                value={assetLoadToSave?.accessorialCost ?? 'x'}
                                onChange={element => { handleOnChangeAccessorialCost(element.target.value) }}
                                onKeyDown={blockInvalidChar}
                                onWheel={event => event.currentTarget.blur()}
                                disabled={loadType === 'Edit' && !isEditLoadEnabled}
                            />
                        </Col>
                    </Row>
                </div>
            </div>

            <div className='AddAssetDetails-Container'>
                <div className='AddAssetDetails-Header'>
                    Driver Details
                </div>

                <div className='AddAssetDetails-Body'>
                    <Row>
                        <Col md={3} className='AddAssetDetails-Field'>
                            <SearchableDropdown
                                dropdownOptions={driverDropdownOptions}
                                id="driver"
                                label="DRIVER"
                                onSelect={value => { handleOnChangeDriver(parseInt(value ?? '')) }}
                                value={assetLoadToSave?.driverName}
                                autoCompleteEnabled
                                endIcon={searchIcon}
                                disabled={driverFieldsDisabled}
                            />
                        </Col>
                        <Col md={3} className='AddAssetDetails-Field'>
                            <div className="Textfield-InputTitle input-group-text AddAssetDetails-InputNumber-Container">
                                TRUCK ID
                            </div>
                            <input
                                type="text"
                                title="truckId"
                                className="AddAssetDetails-Number AddAssetDetails-InputNumber"
                                value={assetLoadToSave?.truckNumber ?? ''}
                                onChange={element => { handleOnChangeTruckNumber(element.target.value ?? '') }}
                                disabled={driverFieldsDisabled}
                            />
                        </Col>
                        <Col md={3} className='AddAssetDetails-Field'>
                            <SearchableDropdown
                                dropdownOptions={trailerDropdownOptions}
                                id="trailer"
                                label="TRAILER ID"
                                onSelect={value => { value && handleOnChangeTrailer(parseInt(value)) }}
                                value={assetLoadToSave?.trailer ?? ' '}
                                autoCompleteEnabled
                                endIcon={searchIcon}
                                disabled={driverFieldsDisabled || !assetLoadToSave?.trailerTypeId || trailerDataLoading}
                                required={assetLoadToSave?.driverName ? true : false}
                                hasError={isTrailerInvalid()}
                                errorMessage='The trailer id does not match this loads trailer type'
                            />
                        </Col>
                    </Row>

                    {loadType !== "Edit" && (
                        <div className="AddAssetDetails-Notes-Container">
                            <div className='AddAssetDetails-Header'>Notes</div>
                            <textarea
                                className="AddAssetDetails-Notes-InputContainer"
                                onChange={event => handleOnChangeNotes(event.target.value)}
                                disabled={!isAddEditAssetLoadNotesEnabled}
                            />
                        </div>
                    )}
                </div>
            </div>
        </>
    )
}

export default AddLoadDriverDetails;